import { globalMessages } from '../../global-message';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  Inject,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, map } from 'rxjs';
import { ButtonComponent } from '@pacificspecialtyinsurancecompany/psic-ng-components';
import { MatIcon } from '@angular/material/icon';
import { ResponsiveService } from '../../services/responsive.service';
import { MENU_CONFIGS, MenuConfig } from '../../menu-config';
import { BILLING_CARD_CONFIGS } from '../../../features/billing/billing-activities/billing-card-configs';
import { DOCUMENT_CARD_CONFIGS } from '../../../features/document-upload/document-activities/document-card-configs';
import { SNL_CARD_CONFIGS } from '../../../features/SNL/snl-activities/snl-card-configs';
import { StepUpAuthService } from '../../../shared/services/step-up-auth.service';
import { APP_CONFIG, AppConfig } from '../../../../app.config';
import { MakePaymentVisibilityService } from '../../services/make-payment-visibility.service';
import { PortletService } from '../../../features/billing/services/portlet.service';
import { UserAccessService } from '../../services/user-access.service';
import { ModalService } from '../../services/modal.service';
@Component({
  selector: 'app-side-menu',
  standalone: true,
  imports: [CommonModule, ButtonComponent, MatIcon],
  templateUrl: './navigation-menu.component.html',
  styleUrls: ['./navigation-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavigationMenuComponent {
  private readonly responsive = inject(ResponsiveService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly stepUpAuth = inject(StepUpAuthService);
  private readonly store = inject(Store);
  private readonly makePaymentVisibility = inject(MakePaymentVisibilityService);
  private readonly portletService = inject(PortletService);
  readonly userAccess = inject(UserAccessService);
  private modalService = inject(ModalService);

  readonly showMakePayment = this.makePaymentVisibility.showMakePayment;

  readonly zipCode = this.stepUpAuth.getZipcode();

  /** Policy data map from the ngrx policy store. */
  private readonly storeData = toSignal(
    this.store.select((state: any) => state.policy?.data),
    { initialValue: null }
  );

  readonly isMobile = toSignal(this.responsive.isMobile$, { initialValue: false });

  /** Emits the current URL path on every navigation — declared early so computed below can use it */
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event) => (event as NavigationEnd).urlAfterRedirects)
    ),
    { initialValue: this.router.url }
  );

  private readonly isDocumentUpload = computed(() =>
    this.currentUrl()?.includes('/documentupload') ?? false
  );

  private readonly isSnl = computed(() =>
    this.currentUrl()?.includes('/snl') ?? false
  );

  /** For documentupload/snl sessionID flow, policyNumber arrives after construction — use signal.
   *  For billing, policyNumber is set before navigation — plain string is fine. */
  readonly displayPolicyNumber = computed(() =>
    this.isDocumentUpload() || this.isSnl()
      ? this.stepUpAuth.policyNumberSignal()
      : this.stepUpAuth.getPolicyNumber()
  );

  /** Policy snapshot for the current policy, or null until it loads. */
  private readonly policySnapshot = computed(() => {
    const pn = this.displayPolicyNumber();
    return this.storeData()?.[pn]?.policySnapshot ?? null;
  });

  readonly propertyAddress = computed(() => {
    const policy = this.policySnapshot();
    if (!policy) return null;
    return policy.propertyAddressInfo?.fullAddress ?? '';
  });

  /** Derives the active menu config based on the current URL */
  private readonly activeConfig = computed<MenuConfig | null>(() => {
    const url = this.currentUrl() || '';
    for (const [segment, config] of Object.entries(MENU_CONFIGS)) {
      if (url.includes(`/${segment}`)) {
        return config;
      }
    }
    return null;
  });

  readonly menuHeader = computed(() => this.activeConfig()?.header ?? '');
  readonly menuItems = computed(() => this.activeConfig()?.items ?? []);
  readonly isBilling = computed(() => this.currentUrl()?.includes('/billing') ?? false);

  readonly selectedItem = signal('Statement');
  readonly hoveredItem = signal<string | null>(null);

  constructor(@Inject(APP_CONFIG) private config: AppConfig) {
    // Set default selected item when config changes due to navigation
    // This is handled via the computed — on first load the billing default is used
    const menuConfig = this.activeConfig();
    if (menuConfig) {
      this.selectedItem.set(menuConfig.defaultSelected);
    }
  }

  /** Map menu labels to card section IDs (billing + documentupload + snl) */
  private readonly menuLabelToSectionId: Record<string, string> = [
    ...BILLING_CARD_CONFIGS,
    ...DOCUMENT_CARD_CONFIGS,
    ...SNL_CARD_CONFIGS(false),
  ].reduce(
    (acc, config) => {
      acc[config.menuLabel] = config.name;
      return acc;
    },
    {} as Record<string, string>
  );

  selectMenuItem(item: string): void {
    this.selectedItem.set(item);
    const sectionId = this.menuLabelToSectionId[item];
    if (sectionId) {
      const currentUrl = this.router.url.split('#')[0].split('?')[0];
      const homeRoute = currentUrl.includes('/documentupload')
        ? globalMessages.routeDocumentUploadHome
        : currentUrl.includes('/snl')
        ? globalMessages.routeSnlHome
        : globalMessages.routeBillingHome;
      if (currentUrl.endsWith('/home')) {
        this.router.navigate([], {
          relativeTo: this.route,
          fragment: sectionId,
          queryParamsHandling: 'preserve',
        });
      } else {
        this.router.navigate([homeRoute], {
          fragment: sectionId,
          queryParamsHandling: 'preserve',
        });
      }
    }
  }

  onMenuItemHover(item: string): void {
    this.hoveredItem.set(item);
  }

  onMenuItemLeave(): void {
    this.hoveredItem.set(null);
  }

  getMenuItemState(item: string): string {
    if (this.selectedItem() === item) {
      return 'selected';
    }
    if (this.hoveredItem() === item) {
      return 'hovered';
    }
    return 'default';
  }

  isMenuItemActive(item: string): boolean {
    return this.selectedItem() === item || this.hoveredItem() === item;
  }

  navigateToMakePayment(): void {
    if (!this.userAccess.hasEditableAccess()) {
      return;
    }

    if (this.userAccess.hasFullAccess()) {
      this.portletService.updateAudit('RedirectToMakePayment');
      this.router.navigate([globalMessages.routeMakePayment], {
        queryParamsHandling: 'preserve',
      });
      return;
    }

    // User does not have full access: ask whether they want to verify their
    // identity (OTP) before paying, or continue to Guest Pay without verification.
    this.openValidationChoiceModal();
  }

  /**
   * Opens the modal asking the user to either proceed with identity
   * verification or continue without it.
   */
  private openValidationChoiceModal(): void {
    const popupDialog = {
      type: 'actionDialog',
      imageSrc: '',
      message: globalMessages.makePaymentValidationTitle,
      messageExt: globalMessages.makePaymentValidationMessage,
      confirm: globalMessages.makePaymentValidationProceed,
      cancel: globalMessages.makePaymentValidationSkip,
      fields: [],
    };

    this.modalService.confirm(popupDialog).subscribe((popupCallbackDetail) => {
      if (popupCallbackDetail[0] === 'Continue') {
        this.proceedWithValidation();
      } else {
        this.proceedWithoutValidation();
      }
    });
  }

  /**
   * User chose to verify their identity: close the modal and route to the OTP page.
   */
  proceedWithValidation(): void {
    this.portletService.updateAudit('RedirectToOtpForMakePayment');
    this.router.navigate([globalMessages.routeOtp], {
      queryParamsHandling: 'preserve',
    });
  }

  /**
   * User chose to skip verification: close the modal and redirect to Guest Pay.
   */
  proceedWithoutValidation(): void {
    const guestPayUrl =
      this.config.guestPayDomainURL +
      globalMessages.guestPayMakePaymentURL +
      '?Category=PolicyNumber&search=' +
      this.stepUpAuth.getPolicyNumber() +
      '&Zipcode=' +
      this.stepUpAuth.getZipcode();
    this.portletService.updateAudit('RedirectToGPP');
    window.open(guestPayUrl, '_blank');
  }

  makePayment(): void {
    this.portletService.updateAudit('RedirectToMakePayment');
    this.router.navigate([globalMessages.routeMakePayment], {
      queryParamsHandling: 'preserve',
    });
  }
}
